// src/accounts/pages/AccountBilling.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../auth/AuthContext";

const EMPTY = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  postcode: "",
  country: "AU",
};

export default function AccountBilling() {
  const { user, refresh } = useAuth();

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);

  const [editMode, setEditMode] = useState(false);
  const [pw, setPw] = useState("");

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      setErr("");
      setMsg("");
      setLoading(true);

      // keep your existing endpoints as-is
      const { data } = await api.get("billing/me");
      if (!data?.ok) throw new Error(data?.message || "Failed to load billing");

      setForm({
        email: data.email || user?.email || "",
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        phone: data.phone || "",
        address1: data.address1 || "",
        address2: data.address2 || "",
        city: data.city || "",
        state: data.state || "",
        postcode: data.postcode || "",
        country: data.country || "AU",
      });
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Could not load billing info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (err) setErr("");
    if (msg) setMsg("");
  };

  const beginEdit = () => {
    setErr("");
    setMsg("");
    setPw("");
    setEditMode(true);
  };

  const cancelEdit = async () => {
    setEditMode(false);
    setPw("");
    await load();
  };

  const save = async () => {
    try {
      setErr("");
      setMsg("");

      if (!pw) {
        setErr("Please confirm your password to edit billing info.");
        return;
      }

      setSaving(true);

      const payload = {
        password: pw,
        firstName: String(form.firstName || "").trim(),
        lastName: String(form.lastName || "").trim(),
        phone: String(form.phone || "").trim(),
        address1: String(form.address1 || "").trim(),
        address2: String(form.address2 || "").trim(),
        city: String(form.city || "").trim(),
        state: String(form.state || "").trim(),
        postcode: String(form.postcode || "").trim(),
        country: String(form.country || "AU").trim().toUpperCase(),
      };

      const { data } = await api.post("billing/update", payload);
      if (!data?.ok) throw new Error(data?.message || "Save failed");

      setMsg("Billing info updated.");
      setEditMode(false);
      setPw("");

      await refresh();
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Could not save billing info");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 8 }}>Loading…</div>;

  const subStatus = String(user?.subscriptionStatus || user?.subscription?.status || "none");
  const isActive = subStatus.toLowerCase() === "active";
  const planAccess = isActive ? "Active" : "Inactive (upgrade to Premium)";

  return (
    <div className="accPage">
      <div className="accPageTitle">Billing info</div>

      {err ? <div className="accAlert accAlertError">{err}</div> : null}
      {msg ? <div className="accAlert">{msg}</div> : null}

      <div className="accPanel">
        <div className="accGrid2">
          <div className="accField">
            <div className="accLabel">Subscription status</div>
            <div className="accReadBox">{subStatus}</div>
          </div>

          <div className="accField">
            <div className="accLabel">Plan access</div>
            <div className="accReadBox">{planAccess}</div>

            {!isActive ? (
              <div style={{ marginTop: 8, fontSize: 13 }}>
                <Link to="/subscribe" style={{ textDecoration: "underline", fontWeight: 800 }}>
                  View Premium plans
                </Link>
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ height: 14 }} />

        <div className="accSectionTitle">Billing details</div>

        <div className="accField">
          <div className="accLabel">Email</div>
          <div className="accReadBox">{form.email || user?.email || ""}</div>
        </div>

        <div className="accGrid2">
          <div className="accField">
            <div className="accLabel">First name</div>
            <input
              className="accInput"
              disabled={!editMode}
              value={form.firstName || ""}
              onChange={(e) => set("firstName", e.target.value)}
            />
          </div>

          <div className="accField">
            <div className="accLabel">Last name</div>
            <input
              className="accInput"
              disabled={!editMode}
              value={form.lastName || ""}
              onChange={(e) => set("lastName", e.target.value)}
            />
          </div>
        </div>

        <div className="accField">
          <div className="accLabel">Phone</div>
          <input
            className="accInput"
            disabled={!editMode}
            value={form.phone || ""}
            onChange={(e) => set("phone", e.target.value)}
          />
        </div>

        <div className="accField">
          <div className="accLabel">Address line 1</div>
          <input
            className="accInput"
            disabled={!editMode}
            value={form.address1 || ""}
            onChange={(e) => set("address1", e.target.value)}
          />
        </div>

        <div className="accField">
          <div className="accLabel">Address line 2</div>
          <input
            className="accInput"
            disabled={!editMode}
            value={form.address2 || ""}
            onChange={(e) => set("address2", e.target.value)}
          />
        </div>

        <div className="accGrid3">
          <div className="accField">
            <div className="accLabel">City</div>
            <input
              className="accInput"
              disabled={!editMode}
              value={form.city || ""}
              onChange={(e) => set("city", e.target.value)}
            />
          </div>

          <div className="accField">
            <div className="accLabel">State</div>
            <input
              className="accInput"
              disabled={!editMode}
              value={form.state || ""}
              onChange={(e) => set("state", e.target.value)}
            />
          </div>

          <div className="accField">
            <div className="accLabel">Postcode</div>
            <input
              className="accInput"
              disabled={!editMode}
              value={form.postcode || ""}
              onChange={(e) => set("postcode", e.target.value)}
            />
          </div>
        </div>

        <div className="accGrid2">
          <div className="accField">
            <div className="accLabel">Country</div>
            <input
              className="accInput"
              disabled={!editMode}
              value={form.country || ""}
              onChange={(e) => set("country", e.target.value)}
            />
          </div>
          <div />
        </div>

        {/* Trust / policy block (helps with Search Console "Deceptive pages") */}
        <div className="accHelpText" style={{ marginTop: 10, lineHeight: 1.45 }}>
          Payment details are processed securely by Stripe. This page only stores your contact/billing profile for invoices.
          <div style={{ marginTop: 6 }}>
            <Link to="/privacy-Policy" style={{ textDecoration: "underline" }}>Privacy Policy</Link>
            {" · "}
            <Link to="/contact" style={{ textDecoration: "underline" }}>Contact</Link>
            {" · "}
            <Link to="/aboutus" style={{ textDecoration: "underline" }}>About</Link>
          </div>
        </div>

        {editMode ? (
          <div className="accField">
            <div className="accLabel">Confirm password to save</div>
            <input
              className="accInput"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
            <div className="accHelpText">
              For security, billing edits require password confirmation.
            </div>
          </div>
        ) : null}

        <div className="accActionsRow">
          {!editMode ? (
            <button type="button" className="accBtn accBtnPrimarySub accBtnMin" onClick={beginEdit}>
              Edit
            </button>
          ) : (
            <>
              <button
                type="button"
                className="accBtn accBtnPrimary accBtnMin"
                onClick={save}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>

              <button
                type="button"
                className="accBtn accBtnSecondary accBtnMin"
                onClick={cancelEdit}
                disabled={saving}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}